import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { 
  Handshake, 
  Contact, 
  Plus,
  Search,
  Mail,
  Phone,
  Instagram,
  Twitter,
  Building2,
  Star,
  MessageSquare,
  ExternalLink
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BrandsPage = ({ user, logout, token }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchContacts();
  }, [token]);

  const fetchContacts = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${BACKEND_URL}/api/contacts`, { headers });
      setContacts(response.data || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const roleColors = {
    influencer: "bg-purple-500/20 text-purple-400",
    brand: "bg-blue-500/20 text-blue-400",
    collaborator: "bg-green-500/20 text-green-400",
    agency: "bg-orange-500/20 text-orange-400",
    client: "bg-cyan-500/20 text-cyan-400"
  };

  const roleLabels = {
    influencer: "Influencer",
    brand: "Marca",
    collaborator: "Colaborador",
    agency: "Agencia",
    client: "Cliente"
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20">
                  <Handshake className="w-6 h-6 text-primary" />
                </div>
                <h1 className="text-3xl font-bold">Brands</h1>
              </div>
              <p className="text-muted-foreground">
                Gestiona tus contactos con marcas y colaboradores
              </p>
            </div>
            <Button className="gap-2" onClick={() => toast.info("Función disponible en Cuentas > Contactos")}>
              <Plus className="w-4 h-4" />
              Nuevo Contacto
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar contactos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-primary">{contacts.length}</div>
                <p className="text-sm text-muted-foreground">Total Contactos</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-400">
                  {contacts.filter(c => c.role === 'brand').length}
                </div>
                <p className="text-sm text-muted-foreground">Marcas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-400">
                  {contacts.filter(c => c.role === 'collaborator').length}
                </div>
                <p className="text-sm text-muted-foreground">Colaboradores</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-cyan-400">
                  {contacts.filter(c => c.role === 'influencer').length}
                </div>
                <p className="text-sm text-muted-foreground">Influencers</p>
              </CardContent>
            </Card>
          </div>

          {/* Contacts List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Cargando contactos...
            </div>
          ) : filteredContacts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Contact className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No hay contactos aún</h3>
                <p className="text-muted-foreground mb-4">
                  Añade contactos de marcas y colaboradores para gestionar tus relaciones
                </p>
                <Button variant="outline" onClick={() => toast.info("Ve a Cuentas > Contactos para añadir")}>
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir primer contacto
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredContacts.map((contact) => (
                <Card key={contact.contact_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {contact.name?.charAt(0)?.toUpperCase() || 'C'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{contact.name}</h3>
                        <Badge className={`text-xs ${roleColors[contact.role] || 'bg-muted'}`}>
                          {roleLabels[contact.role] || contact.role}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mt-4 space-y-2 text-sm">
                      {contact.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{contact.email}</span>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="w-4 h-4" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                      {contact.social_handles?.instagram && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Instagram className="w-4 h-4" />
                          <span>{contact.social_handles.instagram}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="outline" className="flex-1">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Mensaje
                      </Button>
                      <Button size="sm" variant="ghost">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BrandsPage;
